import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageTemplate from '../PageTemplate';
import * as api from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';
import SectionRenderer from './SectionRenderer';
import './InsightDetail.css';

const formatDate = (dateStr, lang) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(lang === 'zh_CN' ? 'zh-CN' : 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });
};

const InsightDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getCurrentLang = () => {
    return i18n.language === 'zh-CN' ? 'zh_CN' : 'en_US';
  };

  const lang = getCurrentLang();

  useEffect(() => {
    const fetchInsight = async () => {
      try {
        setLoading(true);
        const data = await api.getInsightBySlug(slug);
        setInsight(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching insight:', err);
        setError('Failed to load insight article');
      } finally {
        setLoading(false);
      }
    };

    fetchInsight();
  }, [slug]);

  if (loading) {
    return (
      <PageTemplate>
        <LoadingSpinner />
      </PageTemplate>
    );
  }

  if (error || !insight) {
    return (
      <PageTemplate>
        <ErrorMessage message={error || 'Article not found'} />
      </PageTemplate>
    );
  }

  const breadcrumbs = [
    { labelKey: 'nav.insights', href: '/#insights' },
    { labelKey: 'insights.featuredInsights', href: '/insights/featured' },
    { label: insight.title[lang] }
  ];

  return (
    <PageTemplate 
      breadcrumbs={breadcrumbs}
      customTitle={insight.title[lang]}
    >
      <article className="insight-detail">
        {/* 文章头部 */}
        <header className="insight-header">
          <div className="insight-meta">
            <span className="insight-author">{insight.author[lang]}</span>
            <span className="insight-date">{formatDate(insight.publish_date, lang)}</span>
            <span className="insight-read-time">{insight.read_time} {t('common.minutesRead')}</span>
          </div>

          {insight.tags && insight.tags.length > 0 && (
            <div className="insight-tags">
              {insight.tags.map((tag, index) => (
                <span key={index} className="insight-tag">
                  {tag[lang]}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* 封面图 */}
        {insight.cover_image && (
          <div className="insight-cover">
            <img src={insight.cover_image} alt={insight.title[lang]} />
          </div>
        )}

        {/* 摘要 */}
        <div 
          className="insight-summary"
          dangerouslySetInnerHTML={{ __html: insight.summary[lang] }}
        />

        {/* 文章内容 */}
        <div className="insight-content">
          {(() => {
            if (!insight.content) {
              return <div style={{padding: '40px', textAlign: 'center', color: '#666'}}>
                <p>该文章暂无内容</p>
                <p style={{fontSize: '0.9em', marginTop: '10px'}}>文章可能正在编辑中，请稍后再试</p>
              </div>;
            }
            
            if (insight.content.sections && Array.isArray(insight.content.sections) && insight.content.sections.length > 0) {
              // 如果是sections格式，使用原有的SectionRenderer
              return insight.content.sections.map((section, index) => (
                <SectionRenderer key={index} section={section} lang={lang} />
              ));
            }
            
            if (insight.content.zh_CN || insight.content.en_US) {
              // 如果是Markdown格式，使用ReactMarkdown渲染
              const content = insight.content[lang] || insight.content.zh_CN || insight.content.en_US || '';
              if (!content || content.trim() === '') {
                return <div style={{padding: '40px', textAlign: 'center', color: '#666'}}>
                  <p>该文章内容为空</p>
                </div>;
              }
              return (
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 style={{marginTop: '2em', marginBottom: '1em'}} {...props} />,
                    h2: ({node, ...props}) => <h2 style={{marginTop: '1.5em', marginBottom: '0.75em'}} {...props} />,
                    h3: ({node, ...props}) => <h3 style={{marginTop: '1.25em', marginBottom: '0.5em'}} {...props} />,
                    p: ({node, ...props}) => <p style={{marginBottom: '1em', lineHeight: '1.8', textAlign: 'left'}} {...props} />,
                    ul: ({node, ...props}) => <ul style={{marginBottom: '1em', paddingLeft: '2em'}} {...props} />,
                    ol: ({node, ...props}) => <ol style={{marginBottom: '1em', paddingLeft: '2em'}} {...props} />,
                    li: ({node, ...props}) => <li style={{marginBottom: '0.5em', textAlign: 'left'}} {...props} />,
                    img: ({node, ...props}) => <img style={{maxWidth: '100%', height: 'auto', margin: '1em 0'}} {...props} />,
                    blockquote: ({node, ...props}) => <blockquote style={{borderLeft: '4px solid #ddd', paddingLeft: '1em', margin: '1em 0', fontStyle: 'italic'}} {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline ? 
                        <code style={{background: '#f5f5f5', padding: '0.2em 0.4em', borderRadius: '3px', fontSize: '0.9em'}} {...props} /> :
                        <code style={{display: 'block', background: '#f5f5f5', padding: '1em', borderRadius: '5px', overflowX: 'auto', marginBottom: '1em'}} {...props} />
                  }}
                >
                  {content}
                </ReactMarkdown>
              );
            }
            
            if (insight.content[lang]) {
              // 纯文本
              return <p>{insight.content[lang]}</p>;
            }
            
            return <div style={{padding: '40px', textAlign: 'center', color: '#666'}}>
              <p>该文章暂无内容</p>
              <p style={{fontSize: '0.9em', marginTop: '10px'}}>内容格式不支持或正在编辑中</p>
            </div>;
          })()}
        </div>

      </article>
    </PageTemplate>
  );
};

export default InsightDetail;
